import { CommandKind } from "../commands/CommandBuffer";
import {
  DROP_QUERY_PADDING,
  DROP_TRAVEL_FACTOR,
  SPLAT_MAX_SQUASH,
  SPLAT_SQUASH_STEP,
} from "../core/constants";
import type { GameContext } from "../core/GameContext";
import { EventKind } from "../events/EventQueue";

export class CollisionSystem {
  private context!: GameContext;
  private dropId = -1;
  private consumed = false;
  private readonly testCandidate = (slimeId: number): void => {
    const { drops, slimes, events, commands } = this.context;
    const dropId = this.dropId;
    // 一发只命中一个：命中即请求回收并置 consumed，因此不需要按史莱姆槽位去重的位图。
    // 原实现的 hitMask 是为可穿透与可反弹的蓄力弹准备的，两者已随决策 0016 移除。
    if (
      this.consumed ||
      slimes.active[slimeId] === 0 ||
      slimes.isDying(slimeId)
    )
      return;
    const dx = slimes.x[slimeId] - drops.x[dropId];
    const dy = slimes.y[slimeId] - drops.y[dropId];
    const radius = slimes.radius[slimeId] + drops.radius[dropId];
    if (dx * dx + dy * dy > radius * radius) return;
    // 只广播接触事实。命中的后果（T045 起为体积转移与托举冲力）由 mechanics 层解析，
    // 本系统不做玩法裁决。挤压形变与接触推力属于接触反馈，保留在此。
    events.push(
      EventKind.CollisionDetected,
      dropId,
      slimeId,
      drops.definition[dropId],
      drops.x[dropId],
      drops.y[dropId],
      0,
      drops.recordingEntityId[dropId],
      slimes.recordingEntityId[slimeId],
    );
    // 挤压形变是纯表现，留在此处。命中冲力自 T045 起归 mechanics/pressure——
    // waterImpulse 与 liftFactor 是内容参数，写死在这里就永远拧不动。
    //
    // 形变按**微增量累积**而非每滴满幅。满幅的读法是「被子弹连续击打」，每秒十余次全幅
    // 动画被打断重启，画面因此发钝；水柱冲刷应当是缓慢累积、持续衰减。数值对齐原型。
    slimes.scaleX[slimeId] = Math.min(
      SPLAT_MAX_SQUASH,
      slimes.scaleX[slimeId] + SPLAT_SQUASH_STEP,
    );
    slimes.scaleY[slimeId] = 1 / Math.max(0.62, slimes.scaleX[slimeId]);
    commands.lifecycle.push(CommandKind.DespawnDrop, -1, dropId);
    this.consumed = true;
  };

  update(context: GameContext): void {
    this.context = context;
    for (let id = 0; id < context.drops.capacity; id += 1) {
      if (context.drops.active[id] === 0) continue;
      this.dropId = id;
      this.consumed = false;
      const x = (context.drops.x[id] + context.drops.previousX[id]) * 0.5;
      const y = (context.drops.y[id] + context.drops.previousY[id]) * 0.5;
      const travel = Math.hypot(
        context.drops.x[id] - context.drops.previousX[id],
        context.drops.y[id] - context.drops.previousY[id],
      );
      context.grid.forEachNearby(
        x,
        y,
        context.drops.radius[id] +
          DROP_QUERY_PADDING +
          travel * DROP_TRAVEL_FACTOR,
        this.testCandidate,
      );
    }
  }
}
